import { useSyncExternalStore } from 'react'

// Tiny hash router — avoids a routing dependency (spec: keep deps minimal).
// Routes:
//   #/                       client list
//   #/client/new             create client (settings form)
//   #/client/:id             tracker (grid + dashboard)  [main screen]
//   #/client/:id/settings    client settings
//   #/client/:id/invoices    invoice list
//   #/invoice/:id            invoice view (printable)
//   #/notes                  notes (sections + pages)
//   #/notes/:id              a specific note page
//   #/chat                   chat with history
//   #/chat/:id               a specific conversation
//   #/settings               global settings + export/import

function subscribe(cb: () => void) {
  window.addEventListener('hashchange', cb)
  return () => window.removeEventListener('hashchange', cb)
}
function getSnapshot() {
  return window.location.hash || '#/'
}

export function useHash(): string {
  return useSyncExternalStore(subscribe, getSnapshot)
}

export function navigate(to: string) {
  window.location.hash = to
}

export interface Route {
  name:
    | 'dashboard'
    | 'today'
    | 'todos'
    | 'clients'
    | 'client-new'
    | 'tracker'
    | 'client-settings'
    | 'client-invoices'
    | 'client-notes'
    | 'invoice'
    | 'notes'
    | 'chat'
    | 'settings'
    | 'not-found'
  id?: number
}

export function parseRoute(hash: string): Route {
  const path = hash.replace(/^#/, '')
  const parts = path.split('/').filter(Boolean) // e.g. ['client','3','settings']
  if (parts.length === 0) return { name: 'dashboard' }
  if (parts[0] === 'today') return { name: 'today' }
  if (parts[0] === 'todos') {
    const id = parts[1] != null ? Number(parts[1]) : undefined
    return { name: 'todos', id: Number.isFinite(id as number) ? id : undefined }
  }
  if (parts[0] === 'clients') return { name: 'clients' }
  if (parts[0] === 'notes') {
    const id = parts[1] != null ? Number(parts[1]) : undefined
    return { name: 'notes', id: Number.isFinite(id as number) ? id : undefined }
  }
  if (parts[0] === 'chat') {
    const id = parts[1] != null ? Number(parts[1]) : undefined
    return { name: 'chat', id: Number.isFinite(id as number) ? id : undefined }
  }
  if (parts[0] === 'settings') return { name: 'settings' }
  if (parts[0] === 'client') {
    if (parts[1] === 'new') return { name: 'client-new' }
    const id = Number(parts[1])
    if (!Number.isFinite(id)) return { name: 'not-found' }
    if (parts[2] === 'settings') return { name: 'client-settings', id }
    if (parts[2] === 'invoices') return { name: 'client-invoices', id }
    if (parts[2] === 'notes') return { name: 'client-notes', id }
    return { name: 'tracker', id }
  }
  if (parts[0] === 'invoice') {
    const id = Number(parts[1])
    if (!Number.isFinite(id)) return { name: 'not-found' }
    return { name: 'invoice', id }
  }
  return { name: 'not-found' }
}
